import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useState, useEffect } from 'react';
import { cn, isValidEmail } from '@/lib/utils';
import { useSystemConfig } from '@/config/systemConfig';
import { useAuth } from '@/contexts/AuthContext';
import type { ContentFormData } from '..';
import { Card } from '@/components/ui/card';
import { Video, Info, Pencil } from 'lucide-react';
import { findCatalogModel, useModelCatalog } from '@/hooks/useModelCatalog';

interface SettingsProps {
  formData: ContentFormData;
  updateFormData: (data: Partial<ContentFormData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export function Settings({ formData, updateFormData, onValidationChange }: SettingsProps) {
  const [email, setEmail] = useState(formData.email || '');
  const { emailDeliveryRequired } = useSystemConfig();
  const { user } = useAuth();
  
  const FLEX_MIN = 5;
  const FLEX_MAX = 15;
  const MAX_SCENES = 20;

  const [sceneCount, setSceneCount] = useState(formData.sceneCount || 3);
  const [sceneLength, setSceneLength] = useState(formData.sceneLength || 10);
  // For flexible models, total length is the primary control (pacing varies the
  // per-scene durations); scene count is secondary and clamped to keep each
  // scene within 5–15s.
  const [totalLength, setTotalLength] = useState(
    formData.duration || (formData.sceneCount || 3) * (formData.sceneLength || 10),
  );

  // The chosen model dictates which durations are even valid (Kling 5/10,
  // Veo 5-8); empty means flexible within the 5-15s range.
  const { data: modelCatalog } = useModelCatalog();
  const selectedModel = findCatalogModel(modelCatalog?.groups, formData.visualModel);
  const allowedDurations = selectedModel?.duration_options ?? [];
  const hasFixedDurations = allowedDurations.length > 0;

  // Flexible: total drives everything; scene count is constrained so the derived
  // per-scene length stays in [5, 15]s.
  const totalDuration = hasFixedDurations ? sceneCount * sceneLength : totalLength;
  const flexCountMin = Math.max(1, Math.ceil(totalLength / FLEX_MAX));
  const flexCountMax = Math.min(MAX_SCENES, Math.floor(totalLength / FLEX_MIN));

  // Snap an out-of-range seeded length onto the model's allowed set on mount.
  useEffect(() => {
    if (!hasFixedDurations || allowedDurations.includes(sceneLength)) return;
    const snapped = allowedDurations.reduce((a, b) =>
      Math.abs(b - sceneLength) < Math.abs(a - sceneLength) ? b : a,
    );
    setSceneLength(snapped);
    updateFormData({ sceneLength: snapped, duration: sceneCount * snapped });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFixedDurations]);

  // Flexible: keep scene count within the range valid for the chosen total
  // length, and the derived per-scene length in sync.
  useEffect(() => {
    if (hasFixedDurations) return;
    const clamped = Math.min(Math.max(sceneCount, flexCountMin), flexCountMax);
    const length = Math.round(totalLength / clamped);
    if (clamped !== sceneCount || length !== sceneLength) {
      setSceneCount(clamped);
      setSceneLength(length);
      updateFormData({ duration: totalLength, sceneCount: clamped, sceneLength: length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFixedDurations, flexCountMin, flexCountMax]);

  useEffect(() => {
    const isEmailValid = !emailDeliveryRequired || Boolean(email && isValidEmail(email));
    onValidationChange(isEmailValid);
  }, [email, emailDeliveryRequired, onValidationChange]);

  useEffect(() => {
    useSystemConfig.getState().setEmailDeliveryRequired(!user);
  }, [user]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    updateFormData({ email: value });
  };

  const handleSceneCountChange = (value: number[]) => {
    const newCount = value[0];
    setSceneCount(newCount);
    updateFormData({
      sceneCount: newCount,
      sceneLength,
      duration: newCount * sceneLength,
    });
  };

  const handleSceneLengthChange = (value: number[]) => {
    const newLength = value[0];
    setSceneLength(newLength);
    updateFormData({
      sceneCount,
      sceneLength: newLength,
      duration: sceneCount * newLength,
    });
  };

  // Flexible models: total length is primary; clamp scene count to keep
  // per-scene 5–15s, and derive the per-scene length.
  const handleTotalChange = (value: number[]) => {
    const total = value[0];
    const min = Math.max(1, Math.ceil(total / FLEX_MAX));
    const max = Math.min(MAX_SCENES, Math.floor(total / FLEX_MIN));
    const count = Math.min(Math.max(sceneCount, min), max);
    const length = Math.round(total / count);
    setTotalLength(total);
    setSceneCount(count);
    setSceneLength(length);
    updateFormData({ duration: total, sceneCount: count, sceneLength: length });
  };

  const handleFlexCountChange = (value: number[]) => {
    const count = value[0];
    const length = Math.round(totalLength / count);
    setSceneCount(count);
    setSceneLength(length);
    updateFormData({ duration: totalLength, sceneCount: count, sceneLength: length });
  };

  return (
    <div className="max-w-[640px] mx-auto space-y-6 md:space-y-8">
      {/* Scene count / length don't apply to upload mode — durations are set
          per image on the upload step. */}
      {formData.creationMode !== 'upload' && (
      <Card className="p-4 md:p-6 border-muted/60 shadow-sm">
        <h3 className="text-base md:text-lg font-medium mb-6 flex items-center">
          <Video className="h-4 w-4 md:h-5 md:w-5 mr-2 text-primary/80" />
          Video Structure
        </h3>

        {hasFixedDurations ? (
          <>
            {/* Scene count + model-fixed per-scene length (Kling 5/10, Veo 5-8) */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Number of Scenes</Label>
                <span className="text-2xl font-semibold text-primary">{sceneCount}</span>
              </div>
              <Slider
                value={[sceneCount]}
                onValueChange={handleSceneCountChange}
                min={1}
                max={20}
                step={1}
                className="py-2"
                aria-label="Number of scenes"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>10</span>
                <span>20</span>
              </div>
            </div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Scene Length</Label>
                <span className="text-2xl font-semibold text-primary">{sceneLength}s</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allowedDurations.map(value => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleSceneLengthChange([value])}
                    className={cn(
                      'rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors',
                      sceneLength === value
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border hover:border-primary/40',
                    )}
                  >
                    {value}s
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedModel?.name} only renders {allowedDurations.join(' or ')} second
                clips.
              </p>
            </div>
          </>
        ) : (
          <>
            {/* How long should the video be — the primary control. Pacing then
                varies the per-scene durations; tune them in the storyboard. */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Video Length</Label>
                <span className="text-2xl font-semibold text-primary">~{totalLength}s</span>
              </div>
              <Slider
                value={[totalLength]}
                onValueChange={handleTotalChange}
                min={10}
                max={120}
                step={5}
                className="py-2"
                aria-label="Video length"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>10s</span>
                <span>60s</span>
                <span>120s</span>
              </div>
            </div>
            {/* Roughly how many scenes — clamped so each scene stays 5–15s */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Number of Scenes</Label>
                <span className="text-2xl font-semibold text-primary">{sceneCount}</span>
              </div>
              <Slider
                value={[Math.min(Math.max(sceneCount, flexCountMin), flexCountMax)]}
                onValueChange={handleFlexCountChange}
                min={flexCountMin}
                max={flexCountMax}
                step={1}
                className="py-2"
                aria-label="Number of scenes"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{flexCountMin}</span>
                <span>~{sceneLength}s each</span>
                <span>{flexCountMax}</span>
              </div>
            </div>
          </>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4">
          <div className="p-2 md:p-4 bg-muted/20 rounded-lg text-center">
            <div className="text-[11px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Scenes</div>
            <div className="text-xl md:text-2xl font-semibold">{sceneCount}</div>
          </div>
          
          <div className="p-2 md:p-4 bg-muted/20 rounded-lg text-center">
            <div className="text-[11px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Avg Scene</div>
            <div className="text-xl md:text-2xl font-semibold">~{sceneLength}s</div>
          </div>
          
          <div className="p-2 md:p-4 bg-muted/20 rounded-lg text-center">
            <div className="text-[11px] md:text-sm text-muted-foreground mb-0.5 md:mb-1">Total Duration</div>
            <div className="text-xl md:text-2xl font-semibold">{totalDuration}s</div>
          </div>
        </div>
        
        <div className="space-y-3 bg-muted/10 rounded-lg p-3 md:p-4">
          <div className="flex gap-2">
            <Pencil className="h-4 w-4 mt-0.5 text-primary shrink-0" />
            <div className="space-y-1 md:space-y-1.5">
              <div className="text-sm font-medium">Customisable in Storyboard</div>
              <div className="text-xs md:text-sm text-muted-foreground">
                We'll generate {sceneCount} scene{sceneCount !== 1 ? 's' : ''} (~{totalDuration}s total),
                auto-paced to varying lengths. You can then customise everything in the storyboard editor:
              </div>
            </div>
          </div>
          <ul className="text-xs md:text-sm text-muted-foreground list-disc ml-6 space-y-0.5 md:space-y-1">
            <li>Add or remove scenes</li>
            <li>Adjust scene durations</li>
            <li>Reorder scenes</li>
            <li>Fine-tune scene content</li>
          </ul>
        </div>
      </Card>
      )}

      <div className="p-4 md:p-6 border rounded-lg">
        <Label htmlFor="email" className="text-sm md:text-base font-medium mb-2 block">
          Delivery Email {!user && <span className="text-red-500">*</span>}
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          className={`${email && !isValidEmail(email) ? 'border-red-500' : ''} h-10 md:h-11`}
          required={emailDeliveryRequired}
        />
        {email && !isValidEmail(email) && (
          <div className="text-xs md:text-sm text-red-500 mt-1">
            Please enter a valid email address
          </div>
        )}
        <div className="flex items-start gap-1.5 mt-2">
          <Info className="h-3.5 w-3.5 md:h-4 md:w-4 mt-0.5 shrink-0 text-muted-foreground" />
          <div className="text-xs md:text-sm text-muted-foreground">
            {emailDeliveryRequired
              ? "Email delivery is required for non-authenticated users"
              : "We'll send you a notification when your content is ready (optional)"}
          </div>
        </div>
      </div>
    </div>
  );
}
